import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Camera, Upload, Sparkles, X, PenLine, Undo2, Trash2, Download, Share2, Plus, Repeat, Eraser, Images, RotateCcw, RotateCw, SlidersHorizontal, ImagePlus, Palette, Wand2 } from "lucide-react";
import { resolveMediaUrl, downloadAssetUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { track } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";

const CATEGORIES = ["All", "Sofa", "Chair", "Table", "Bed", "Wardrobe", "Decor", "Lighting", "Curtain", "Tile", "Paint"];
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

const MARKER_COLORS = [
  { key: "black", hex: "#1A1C1E" },
  { key: "red", hex: "#D64545" },
  { key: "green", hex: "#2E9E5B" },
  { key: "blue", hex: "#2D6FD6" },
];

const ACTIONS = [
  { key: "add", label: "Add", icon: Plus, desc: "Place a new product in the room" },
  { key: "replace", label: "Replace", icon: Repeat, desc: "Swap an existing item for a product" },
  { key: "remove", label: "Remove", icon: Eraser, desc: "Take something out of the room" },
  { key: "adjust", label: "Adjust", icon: SlidersHorizontal, desc: "Move, resize or reposition items in the room" },
];

// 3x3-grid cell ("upper-left", "center", …) a single stroke's own bounding-
// box center falls in — matches the backend's `_AREA_WORDS` grid exactly.
function strokeCell(stroke, img) {
  const xs = stroke.points.map((p) => p.x);
  const ys = stroke.points.map((p) => p.y);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2 / (img?.clientWidth || 1);
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2 / (img?.clientHeight || 1);
  const horiz = cx < 0.33 ? "left" : cx > 0.66 ? "right" : "center";
  const vert = cy < 0.33 ? "upper" : cy > 0.66 ? "lower" : "middle";
  return vert === "middle" && horiz === "center" ? "center" : `${vert}-${horiz}`;
}

/** One grid cell per stroke, in stroke order, duplicates included — e.g.
 * marking 3 individual cushions gives 3 entries even if two land in the
 * same cell. Each separate mark (a tap, a small line, a circle — one
 * continuous press-drag-release) is treated as pointing at exactly one
 * item; the backend uses this list's *length* as an explicit item count
 * (e.g. "the user marked 3 cushions, so replace exactly those 3"), so
 * marks must NOT be deduped or merged into a shared bounding box here the
 * way an area-only description would. */
function describeMarkLocations(strokes, img) {
  if (!strokes.length || !img) return null;
  return strokes.filter((s) => s.points.length).map((s) => strokeCell(s, img));
}

/**
 * Shared visualizer UI (upload room photo, mark a spot, pick an action +
 * product(s), generate, download/share) used by both the anonymous QR flow
 * (pages/customer/CustomerVisualizer.jsx) and the authenticated salesperson
 * flow (pages/sales/SalesVisualizer.jsx). The two differ only in how they
 * reach the backend, so that's the one thing injected via `api`:
 *   getRetailer(): Promise<{id, business_name, slug}>
 *   getProducts(category): Promise<Array>
 *   uploadFile(file): Promise<{url}>
 *   submitRender(payload): Promise<{id}>
 *   getRenderStatus(id): Promise<{status, output_urls, error}>
 */
export default function VisualizerCore({ api, notFoundMessage, embedded = false, preloadedRoomUrl }) {
  const { refresh, user } = useAuth();
  const [retailer, setRetailer] = useState(null);
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [selected, setSelected] = useState([]);
  const [colorOverrides, setColorOverrides] = useState({});
  const [imageOverrides, setImageOverrides] = useState({}); // product_id -> chosen ProductImage.id
  const [roomFile, setRoomFile] = useState(null);
  // Version history of the room image: [originalUpload, ...generatedVersions].
  // historyIndex is "current" — undo moves it back without discarding later
  // versions, so a fresh generate() from an undone point truncates them
  // (add chair → add curtains → undo curtains → replace bed drops "curtains").
  const [history, setHistory] = useState(() => {
    try { const s = sessionStorage.getItem("viz_history"); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [historyIndex, setHistoryIndex] = useState(() => {
    try { const s = sessionStorage.getItem("viz_historyIndex"); return s !== null ? Number(s) : -1; } catch { return -1; }
  });
  const roomUrl = historyIndex >= 0 ? history[historyIndex] : "";
  // sceneState mirrors history[] — one entry per version, each entry is the
  // list of product names that are visually present in that version.
  const [sceneState, setSceneState] = useState(() => {
    try { const s = sessionStorage.getItem("viz_sceneState"); return s ? JSON.parse(s) : [[]]; } catch { return [[]]; }
  });
  const [prompt, setPrompt] = useState("");
  const [action, setAction] = useState("add");
  const [rendering, setRendering] = useState(false);
  const [renderResult, setRenderResult] = useState(null); // { output_urls }
  const [lastRenderedUrl, setLastRenderedUrl] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState("");
  const [showCatalog, setShowCatalog] = useState(false);
  const [marking, setMarking] = useState(false);
  const [markerColor, setMarkerColor] = useState("red");
  const [strokes, setStrokes] = useState([]);
  const [colorPickerFor, setColorPickerFor] = useState(null);
  const [imagePickerFor, setImagePickerFor] = useState(null);
  const [paintColor, setPaintColor] = useState(null);
  // "floor" | "wall" | null — asked via a popup right after a Tile-category
  // product is picked for "add" or "replace" (both mean the same thing to
  // the backend prompt for a tile: lay/replace the surface), since the same
  // tile product can go on either surface and the backend needs to know
  // which. Irrelevant (and cleared) for any other action.
  const [tileType, setTileType] = useState(null);
  const [showTileTypeModal, setShowTileTypeModal] = useState(false);
  // "wall" | "ceiling" | null — same idea as tileType but for a Paint-
  // category product: which surface the chosen color goes on.
  const [paintSurface, setPaintSurface] = useState(null);
  const [showPaintSurfaceModal, setShowPaintSurfaceModal] = useState(false);
  // Paint's color isn't picked from the catalog's preset color_palette
  // swatches the way other products' colors are — it's a free-form code
  // entered in its own dedicated popup, since a paint shade isn't
  // meaningfully limited to whatever swatches a retailer happened to list.
  // `paintColorFor` is which selected product's color that popup is
  // currently editing (there's normally only one Paint product selected at
  // a time, but this keeps it unambiguous either way).
  const [showPaintColorModal, setShowPaintColorModal] = useState(false);
  const [paintColorFor, setPaintColorFor] = useState(null);
  // Experimental "remove" path: a real per-pixel brush mask sent to a local
  // IOPaint (LaMa) server for actual erasure, then touched up by Flux — see
  // services/iopaint.py and AIService._render_remove_via_iopaint. Off by
  // default; the existing grid-area + text-prompt remove (straight to
  // ModelsLab) is untouched and still runs whenever this is off.
  const [preciseRemove, setPreciseRemove] = useState(false);
  const [brushSize, setBrushSize] = useState(40); // display px; scaled to the room photo's natural resolution at mask time
  const [showHistory, setShowHistory] = useState(false);
  const [roomRotation, setRoomRotation] = useState(0);
  // Per-iteration metadata: historyMeta[i] = { appliedProducts, appliedPaint } for history[i]
  const [historyMeta, setHistoryMeta] = useState(() => {
    try { const s = sessionStorage.getItem("viz_historyMeta"); return s ? JSON.parse(s) : [{}]; } catch { return [{}]; }
  });

  useEffect(() => {
    try { sessionStorage.setItem("viz_history", JSON.stringify(history)); } catch {}
  }, [history]);
  useEffect(() => {
    try { sessionStorage.setItem("viz_historyIndex", String(historyIndex)); } catch {}
  }, [historyIndex]);
  useEffect(() => {
    try { sessionStorage.setItem("viz_sceneState", JSON.stringify(sceneState)); } catch {}
  }, [sceneState]);
  useEffect(() => {
    try { sessionStorage.setItem("viz_historyMeta", JSON.stringify(historyMeta)); } catch {}
  }, [historyMeta]);
  useEffect(() => {
    return () => {
      try {
        ["viz_history", "viz_historyIndex", "viz_sceneState", "viz_historyMeta"].forEach((k) => sessionStorage.removeItem(k));
      } catch {}
    };
  }, []);
  const cameraRef = useRef(null);
  const imgRef = useRef(null);
  const changeRoomRef = useRef(null);
  const goBack = useCallback(() => window.history.back(), []);

  useEffect(() => {
    api.getRetailer().then(setRetailer).catch(() => setError(notFoundMessage || "Store not found"));
  }, [api, notFoundMessage]);

  // Pre-load a room image passed in from the salesperson queue flow
  useEffect(() => {
    if (preloadedRoomUrl && history.length === 0) {
      setHistory([preloadedRoomUrl]);
      setHistoryIndex(0);
      setSceneState([[]]);
    }
  }, [preloadedRoomUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.getProducts(category === "All" ? undefined : category).then(setProducts).catch(() => {});
  }, [api, category]);

  // tileType/paintSurface only mean anything for "add"/"replace" — dropping
  // them when the user switches away avoids stale "wall"/"floor"/"ceiling"
  // state silently reapplying if they switch back with a different
  // (non-tile/non-paint) product later.
  useEffect(() => {
    if (action !== "replace" && action !== "add") {
      setTileType(null);
      setPaintSurface(null);
    }
    if (action !== "remove") setPreciseRemove(false);
  }, [action]);

  useEffect(() => {
    if (!showCatalog) {
      setColorPickerFor(null);
      setImagePickerFor(null);
      return;
    }
    const onKey = (e) => {
      if (e.key === "Escape") setShowCatalog(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showCatalog]);

  const fixImageOrientation = (file) =>
    new Promise((resolve) => {
      // Read EXIF orientation tag from the raw file bytes, then redraw
      // the image on a canvas with the correct rotation baked in.
      // canvas.drawImage() ignores EXIF, so we must handle it manually.
      const getExifOrientation = (buffer) => {
        const view = new DataView(buffer);
        if (view.getUint16(0, false) !== 0xFFD8) return 1; // not JPEG
        let offset = 2;
        while (offset < view.byteLength) {
          const marker = view.getUint16(offset, false);
          offset += 2;
          if (marker === 0xFFE1) { // APP1 / EXIF
            if (view.getUint32(offset + 2, false) !== 0x45786966) return 1; // no "Exif"
            const little = view.getUint16(offset + 8, false) === 0x4949;
            const ifdOffset = offset + 8 + view.getUint32(offset + 12, little);
            const tags = view.getUint16(ifdOffset, little);
            for (let i = 0; i < tags; i++) {
              if (view.getUint16(ifdOffset + 2 + i * 12, little) === 0x0112)
                return view.getUint16(ifdOffset + 2 + i * 12 + 8, little);
            }
            return 1;
          }
          if ((marker & 0xFF00) !== 0xFF00) break;
          offset += view.getUint16(offset, false);
        }
        return 1;
      };

      const reader = new FileReader();
      reader.onload = (ev) => {
        const orientation = getExifOrientation(ev.target.result);
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const swap = orientation >= 5; // 5-8 swap width/height
          const canvas = document.createElement("canvas");
          canvas.width  = swap ? img.naturalHeight : img.naturalWidth;
          canvas.height = swap ? img.naturalWidth  : img.naturalHeight;
          const ctx = canvas.getContext("2d");
          // Apply the EXIF transform so the output is always upright
          switch (orientation) {
            case 2: ctx.transform(-1, 0, 0,  1, canvas.width, 0); break;
            case 3: ctx.transform(-1, 0, 0, -1, canvas.width, canvas.height); break;
            case 4: ctx.transform( 1, 0, 0, -1, 0, canvas.height); break;
            case 5: ctx.transform( 0, 1, 1,  0, 0, 0); break;
            case 6: ctx.transform( 0, 1,-1,  0, canvas.height, 0); break;
            case 7: ctx.transform( 0,-1,-1,  0, canvas.height, canvas.width); break;
            case 8: ctx.transform( 0,-1, 1,  0, 0, canvas.width); break;
            default: break; // orientation 1 — already correct
          }
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => resolve(blob ? new File([blob], file.name, { type: "image/jpeg" }) : file),
            "image/jpeg", 0.92
          );
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
        img.src = url;
      };
      reader.onerror = () => resolve(file);
      // Only need the first 64 KB — EXIF is always near the start
      reader.readAsArrayBuffer(file.slice(0, 65536));
    });

  const uploadRoom = async (file) => {
    if (!file) return;
    setRoomFile(file);
    track("room_uploaded", { size_kb: Math.round(file.size / 1024), type: file.type });
    try {
      const fixedFile = await fixImageOrientation(file);
      const data = await api.uploadFile(fixedFile);
      setHistory([data.url]);
      setHistoryIndex(0);
      setSceneState([[]]);
      setHistoryMeta([{}]);
      setRenderResult(null);
      setStrokes([]);
      setRoomRotation(0);
      try { sessionStorage.removeItem("viz_history"); sessionStorage.removeItem("viz_historyIndex"); sessionStorage.removeItem("viz_sceneState"); sessionStorage.removeItem("viz_historyMeta"); } catch {}
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const isSurfaceAction = action === "replace" || action === "add";

  const toggle = (id) =>
    setSelected((s) => {
      const adding = !s.includes(id);
      if (adding) {
        track("product_selected", { product_id: id });
        const product = products.find((p) => p.id === id);
        const cat = (product?.category || "").toLowerCase();
        if (isSurfaceAction && cat === "tile") {
          setTileType(null);
          setShowTileTypeModal(true);
        }
        if (isSurfaceAction && cat === "paint") {
          setPaintSurface(null);
          setPaintColorFor(id);
          setShowPaintColorModal(true);
        }
      }
      if (s.includes(id)) {
        setColorOverrides((c) => {
          const next = { ...c };
          delete next[id];
          return next;
        });
        setImageOverrides((c) => {
          const next = { ...c };
          delete next[id];
          return next;
        });
        const next = s.filter((x) => x !== id);
        const categoryOf = (pid) => (products.find((p) => p.id === pid)?.category || "").toLowerCase();
        if (!next.some((pid) => categoryOf(pid) === "tile")) setTileType(null);
        if (!next.some((pid) => categoryOf(pid) === "paint")) setPaintSurface(null);
        if (paintColorFor === id) setPaintColorFor(null);
        return next;
      }
      return [...s, id];
    });

  const setColor = (productId, hex) =>
    setColorOverrides((c) => ({ ...c, [productId]: c[productId] === hex ? undefined : hex }));

  // Unlike setColor (a catalog swatch toggle — clicking the same swatch
  // again deselects it), confirming the paint color popup with the same hex
  // it already had must NOT clear it, so this always sets rather than toggles.
  const setExactColor = (productId, hex) =>
    setColorOverrides((c) => ({ ...c, [productId]: hex }));

  const setImageVariant = (productId, imageId) =>
    setImageOverrides((c) => ({ ...c, [productId]: c[productId] === imageId ? undefined : imageId }));

  // Reset per-edit state (marks, product picks) since they're tied to
  // whichever room-image version was on screen when they were made — used
  // by both "Undo" and "Edit again" since both change that base image.
  const resetEditState = () => {
    setStrokes([]);
    setMarking(false);
    setSelected([]);
    setColorOverrides({});
    setImageOverrides({});
    setPrompt("");
    setAction("add");
    setPaintColor(null);
    setTileType(null);
    setShowTileTypeModal(false);
    setPaintSurface(null);
    setShowPaintSurfaceModal(false);
    setShowPaintColorModal(false);
    setPaintColorFor(null);
    setPreciseRemove(false);
  };

  const applyRotation = async () => {
    if (roomRotation === 0) return;
    const deg = roomRotation;
    const swap = deg === 90 || deg === 270;
    try {
      toast.loading("Applying rotation…", { id: "rotation" });
      // Fetch through the backend proxy — same-origin, no CORS taint
      const resp = await fetch(downloadAssetUrl(roomUrl, "room.jpg"));
      if (!resp.ok) throw new Error("fetch failed");
      const blob = await resp.blob();
      const bitmap = await createImageBitmap(blob);
      const canvas = document.createElement("canvas");
      canvas.width  = swap ? bitmap.height : bitmap.width;
      canvas.height = swap ? bitmap.width  : bitmap.height;
      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((deg * Math.PI) / 180);
      ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
      bitmap.close();
      const outBlob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
      if (!outBlob) throw new Error("toBlob failed");
      const file = new File([outBlob], "rotated_room.jpg", { type: "image/jpeg" });
      const data = await api.uploadFile(file);
      setHistory([data.url]);
      setHistoryIndex(0);
      setSceneState([[]]);
      setHistoryMeta([{}]);
      setRenderResult(null);
      setStrokes([]);
      setRoomRotation(0);
      toast.success("Rotation applied!", { id: "rotation" });
    } catch (e) {
      console.error("applyRotation error", e);
      toast.error("Failed to apply rotation", { id: "rotation" });
    }
  };

  const undo = () => {
    if (historyIndex <= 0) return;
    setHistoryIndex((i) => i - 1);
    // sceneState stays in sync with history — no need to trim it here,
    // historyIndex moving back is enough to read the right context.
    setStrokes([]);
    setRenderResult(null);
    resetEditState();
  };

  const editAgain = () => {
    setRenderResult(null);
    resetEditState();
  };;

  // navigator.share({ url }) shares the Models Lab *link*, not a photo —
  // WhatsApp/Instagram then post it as a text/link card instead of an
  // attached image. Fetching the actual bytes (via the same download proxy
  // used for Download, which already knows how to fetch a Models Lab URL
  // safely) and sharing a real File fixes that: it shows up as an actual
  // .jpg attachment in the share sheet.
  const shareRender = async (imageUrl) => {
    if (!imageUrl) return;
    track("share_clicked");
    setSharing(true);
    try {
      const filename = "neiv-render.jpg";
      const resp = await fetch(downloadAssetUrl(imageUrl, filename));
      if (!resp.ok) throw new Error("Could not fetch the image");
      const blob = await resp.blob();
      const file = new File([blob], filename, { type: blob.type || "image/jpeg" });

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "My NEIV render" });
        return;
      }

      // No file-sharing support here (most desktop browsers, some older
      // mobile ones) — save the actual image locally instead of falling
      // back to sharing the raw link, which is the exact bug this replaces.
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(objectUrl);
      toast.success("Image saved — attach it in WhatsApp/Instagram to share.");
    } catch (err) {
      if (err?.name === "AbortError") return; // user cancelled the native share sheet
      toast.error("Couldn't prepare the image to share");
    } finally {
      setSharing(false);
    }
  };

  // Composite marker strokes onto the room image and upload as a TEMPORARY
  // image for ModelsLab only. The original roomUrl (history[0] or any
  // generated version) is NEVER touched — history always stores clean URLs.
  // Image quality is preserved: canvas draws at full naturalWidth/Height,
  // PNG format when markers exist — lossless, preserves sharp marker edges.
  const compositeMarkedRoom = async (strokesToUse) => {
    if (!strokesToUse.length) return null;
    const imgEl = imgRef.current;
    if (!imgEl) return null;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = imgEl.naturalWidth;
      canvas.height = imgEl.naturalHeight;
      const ctx = canvas.getContext("2d");
      // Draw original room at full resolution — no quality loss
      ctx.drawImage(imgEl, 0, 0, canvas.width, canvas.height);

      // Scale stroke coords (display pixels) → natural image pixels
      const scaleX = imgEl.naturalWidth / imgEl.clientWidth;
      const scaleY = imgEl.naturalHeight / imgEl.clientHeight;
      // Marker width scaled to natural resolution: 16px display → proportional
      const markerWidth = 16 * Math.max(scaleX, scaleY);

      strokesToUse.forEach((s) => {
        if (!s.points.length) return;
        // Single-point tap → draw a filled circle so it's visible
        if (s.points.length === 1) {
          ctx.beginPath();
          ctx.globalAlpha = 0.8;
          ctx.fillStyle = s.color;
          ctx.arc(s.points[0].x * scaleX, s.points[0].y * scaleY, markerWidth, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
          return;
        }
        ctx.beginPath();
        ctx.strokeStyle = s.color;
        ctx.lineWidth = markerWidth;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        // 80% opacity: marker clearly visible even on wooden/light floors
        ctx.globalAlpha = 0.8;
        s.points.forEach((p, i) => {
          const x = p.x * scaleX;
          const y = p.y * scaleY;
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        });
        ctx.stroke();
        ctx.globalAlpha = 1;
      });

      // PNG — lossless, no edge blur on marker strokes
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      if (!blob) return null;
      const file = new File([blob], "marked_room_temp.png", { type: "image/png" });
      const data = await api.uploadFile(file);
      return data.url; // temporary URL — only used for this one render call
    } catch {
      return null;
    }
  };

  // Builds a real per-pixel mask (white = erase, black = keep — IOPaint's
  // own convention) from the brush strokes drawn in "Precise removal"
  // mode, at the room photo's natural resolution. Unlike
  // compositeMarkedRoom (which draws colored strokes ON the room photo, for
  // the coarse grid-area text-prompt path every other action uses), this
  // never touches the room photo itself — it's a separate black/white
  // image sent alongside it to IOPaint.
  const compositeRemovalMask = async (strokesToUse) => {
    if (!strokesToUse.length) return null;
    const imgEl = imgRef.current;
    if (!imgEl) return null;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = imgEl.naturalWidth;
      canvas.height = imgEl.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const scaleX = imgEl.naturalWidth / imgEl.clientWidth;
      const scaleY = imgEl.naturalHeight / imgEl.clientHeight;
      const brushWidthNatural = brushSize * Math.max(scaleX, scaleY);

      ctx.fillStyle = "#ffffff";
      ctx.strokeStyle = "#ffffff";
      strokesToUse.forEach((s) => {
        if (!s.points.length) return;
        if (s.points.length === 1) {
          ctx.beginPath();
          ctx.arc(s.points[0].x * scaleX, s.points[0].y * scaleY, brushWidthNatural / 2, 0, Math.PI * 2);
          ctx.fill();
          return;
        }
        ctx.beginPath();
        ctx.lineWidth = brushWidthNatural;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        s.points.forEach((p, i) => {
          const x = p.x * scaleX;
          const y = p.y * scaleY;
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        });
        ctx.stroke();
      });

      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      if (!blob) return null;
      const file = new File([blob], "removal_mask.png", { type: "image/png" });
      const data = await api.uploadFile(file);
      return data.url;
    } catch {
      return null;
    }
  };

  const generate = async () => {
    if (!roomUrl) return toast.error("Upload a room image first");
    if (action === "adjust" && !prompt.trim()) return toast.error("Describe the adjustment");
    if (action !== "remove" && action !== "adjust" && selected.length === 0) return toast.error("Pick at least one product");
    if (action === "remove" && preciseRemove && strokes.length === 0) {
      return toast.error("Brush over the object you want removed");
    }
    if (action === "remove" && !preciseRemove && selected.length === 0 && strokes.length === 0 && !prompt.trim()) {
      return toast.error("Mark the spot to remove, or describe what to remove");
    }
    const hasSelectedTile = isSurfaceAction && selectedProducts.some((p) => (p.category || "").toLowerCase() === "tile");
    if (hasSelectedTile && !tileType) {
      setShowTileTypeModal(true);
      return toast.error("Choose whether this tile goes on the floor or a wall");
    }
    if (hasSelectedTile && tileType === "wall" && strokes.length === 0) {
      return toast.error("Mark the wall(s) you want tiled first");
    }
    const paintProduct = isSurfaceAction
      ? selectedProducts.find((p) => (p.category || "").toLowerCase() === "paint")
      : null;
    const hasSelectedPaint = !!paintProduct;
    if (hasSelectedPaint && !colorOverrides[paintProduct.id]) {
      setPaintColorFor(paintProduct.id);
      setShowPaintColorModal(true);
      return toast.error("Choose a paint color first");
    }
    if (hasSelectedPaint && !paintSurface) {
      setShowPaintSurfaceModal(true);
      return toast.error("Choose whether to paint the wall or the ceiling");
    }
    if (hasSelectedPaint && paintSurface === "wall" && strokes.length === 0) {
      return toast.error("Mark the wall(s) you want painted first");
    }
    const startedAt = Date.now();
    track("render_started", { action, product_count: selected.length, has_marks: strokes.length > 0, has_prompt: !!prompt.trim() });
    setRendering(true);
    setRenderResult(null);
    setError("");
    const baseIndex = historyIndex;
    try {
      const imgEl = imgRef.current;

      const effectiveStrokes = strokes;

      const mark_locations = describeMarkLocations(effectiveStrokes, imgEl);
      const roomImageToSend = roomUrl;

      const effectivePrompt = prompt.trim();

      // scene_context = names of objects already visible in the current room
      // image (history[historyIndex]). Backend uses this to build the
      // "Image 1 already contains…" prefix so the model knows what to preserve.
      const scene_context = sceneState[historyIndex] ?? [];

      // Experimental: a real per-pixel mask for the IOPaint+Flux remove
      // path (see AIService._render_remove_via_iopaint). Presence of this
      // field is what routes the backend there instead of the default
      // grid-area + text-prompt remove — everything else about the payload
      // stays the same either way.
      let removal_mask_url = null;
      if (action === "remove" && preciseRemove) {
        removal_mask_url = await compositeRemovalMask(effectiveStrokes);
        if (!removal_mask_url) throw new Error("Couldn't prepare the removal mask");
      }

      const payload = {
        room_image_url: roomImageToSend,
        prompt: effectivePrompt,
        action,
        mark_locations: mark_locations?.length ? mark_locations : null,
        mark_color: mark_locations?.length ? markerColor : null,
        has_visual_marker: false,
        tile_type: hasSelectedTile ? tileType : null,
        paint_surface: hasSelectedPaint ? paintSurface : null,
        removal_mask_url,
        product_ids: selected,
        variations: 1,
        color_overrides: colorOverrides,
        image_overrides: imageOverrides,
        scene_context,
      };

      const job = await api.submitRender(payload);
      const result = await pollRender(job.id);
      track("render_completed", { action, product_count: selected.length, duration_ms: Date.now() - startedAt, output_count: result.output_urls?.length ?? 0 });
      const chosen = result.output_urls?.[0];
      if (chosen) {
        refresh(); // update credits in navbar instantly
        api.getRetailer().then(setRetailer).catch(() => {}); // refresh retailer credits
        setHistory((h) => { const next = [...h, chosen]; setHistoryIndex(next.length - 1); return next; });

        setSceneState((prev) => {
          const current = prev[baseIndex] ?? [];
          let next;
          if (action === "add") {
            next = [...current, ...selectedProducts.map((p) => p.name)];
          } else if (action === "replace") {
            next = [...current, ...selectedProducts.map((p) => p.name)];
          } else if (action === "remove" && selectedProducts.length > 0) {
            const removeNames = new Set(selectedProducts.map((p) => p.name.toLowerCase()));
            next = current.filter((n) => !removeNames.has(n.toLowerCase()));
          } else {
            next = [...current];
          }
          return [...prev, next];
        });

        setStrokes([]);
        // Paint has no meaningful product photo to show as a thumbnail —
        // it's shown as a color swatch (appliedPaint) instead, so it's
        // excluded from appliedProducts to avoid showing it twice.
        const paintHex = hasSelectedPaint ? colorOverrides[paintProduct.id] : null;
        const meta = {
          appliedProducts: selectedProducts
            .filter((p) => (p.category || "").toLowerCase() !== "paint")
            .map((p) => ({ id: p.id, name: p.name, image_url: productImageUrl(p) })),
          appliedPaint: hasSelectedPaint && paintHex ? { name: paintProduct.name, hex: paintHex } : paintColor,
        };
        setHistoryMeta((prev) => [...prev, meta]);
        setRenderResult({ ...result, ...meta });
        setLastRenderedUrl(chosen);
      }
    } catch (err) {
      track("render_failed", { action, product_count: selected.length, duration_ms: Date.now() - startedAt });
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setRendering(false);
    }
  };

  const pollRender = useCallback((renderId) =>
    new Promise((resolve, reject) => {
      const TIMEOUT = 600000;
      const FALLBACK_INTERVAL = 4000;
      let done = false;
      let timer = null;
      let ws = null;
      let reconnectTimer = null;
      let reconnectAttempts = 0;
      const MAX_RECONNECTS = 3;

      const finish = (fn) => {
        if (done) return;
        done = true;
        clearInterval(timer);
        clearTimeout(reconnectTimer);
        try { ws?.close(); } catch (_) {}
        fn();
      };

      const onResult = (data) => {
        if (data.type === "ping") {
          try { ws?.send("ping"); } catch (_) {}
          return;
        }
        if (data.status === "completed") {
          finish(() => resolve({ status: "completed", output_urls: data.output_urls }));
        } else if (data.status === "failed") {
          finish(() => reject({ response: { data: { detail: data.error || "Generation failed" } } }));
        }
      };

      let checking = false;
      const startFallbackPoll = () => {
        if (timer) return;
        let elapsed = 0;
        timer = setInterval(async () => {
          if (checking || done) return;
          checking = true;
          try {
            const data = await api.getRenderStatus(renderId);
            onResult(data);
          } catch (_) {}
          checking = false;
          elapsed += FALLBACK_INTERVAL;
          if (elapsed >= TIMEOUT) finish(() => reject({ response: { data: { detail: "Render timed out. Please try again." } } }));
        }, FALLBACK_INTERVAL);
      };

      const connectWs = () => {
        if (done) return;
        try {
          const wsBase = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
          ws = new WebSocket(`${wsBase}/api/ws/${renderId}`);
          ws.onmessage = (e) => {
            try { onResult(JSON.parse(e.data)); } catch (_) {}
          };
          ws.onerror = () => {
            startFallbackPoll();
          };
          ws.onclose = () => {
            if (done) return;
            startFallbackPoll();
            if (reconnectAttempts < MAX_RECONNECTS) {
              reconnectAttempts++;
              const delay = Math.min(2000 * reconnectAttempts, 8000);
              reconnectTimer = setTimeout(connectWs, delay);
            }
          };
          ws.onopen = () => {
            reconnectAttempts = 0;
          };
        } catch (_) {
          startFallbackPoll();
        }
      };

      connectWs();
      setTimeout(() => finish(() => reject({ response: { data: { detail: "Render timed out. Please try again." } } })), TIMEOUT);
    }), [api]);

  const productImageUrl = (p) => {
    const chosenId = imageOverrides[p.id];
    if (chosenId) {
      const match = p.images?.find((img) => img.id === chosenId);
      if (match) return match.url;
    }
    return p.image_url;
  };

  const selectedProducts = useMemo(
    () => products.filter((p) => selected.includes(p.id)),
    [products, selected]
  );

  const selectedPaintProduct = isSurfaceAction
    ? selectedProducts.find((p) => (p.category || "").toLowerCase() === "paint")
    : null;

  const promptPlaceholder =
    action === "remove"
      ? preciseRemove
        ? "Optional: notes for the touch-up, e.g. match the wood floor tone"
        : "Describe what to remove, e.g. the small lamp on the table"
      : action === "adjust"
      ? "Describe the adjustment, e.g. place bed a bit to the right side"
      : "Optional: style details, e.g. warm lighting, Scandinavian style…";

  if (error) {
    return (
      <div className={`${embedded ? "" : "min-h-[100dvh]"} flex items-center justify-center bg-[#FAFAFA] p-8`}>
        <div className="text-center">
          <Logo />
          <h2 className="mt-8 text-2xl font-display font-medium">Store not found</h2>
          <p className="mt-2 text-[#5C5F62]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? "min-h-[calc(100vh-80px)]" : "min-h-[100dvh]"} bg-[#0F1011] text-white flex flex-col relative overflow-hidden rounded-xl`}>
      {/* Top bar */}
      {!embedded && (
      <header className="glass-panel !bg-white/10 !border-white/20 sticky top-0 z-30 px-2 sm:px-4 py-3 grid grid-cols-[auto_1fr_auto] items-center gap-2">
        <div className="flex items-center gap-1 sm:gap-2 min-w-0">
          <button onClick={goBack} className="text-white/80 hover:text-white p-2 shrink-0" data-testid="visualizer-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <Logo variant="light" size="sm" />
        </div>
        <div className="min-w-0 text-center px-1">
          <p className="truncate text-[10px] sm:text-xs uppercase tracking-widest text-white/60">Experience by {retailer?.business_name || "…"}</p>
        </div>
        <div className="text-xs text-white/70 flex items-center gap-1.5 sm:gap-3 justify-self-end">
          {user?.credits != null && (
            <span className="bg-white/10 border border-white/20 rounded-full px-2 sm:px-3 py-1 font-medium whitespace-nowrap">
              {user.credits} credit{user.credits !== 1 ? "s" : ""}
            </span>
          )}
          <span className="flex items-center gap-1.5 shrink-0">
            <span className="h-2 w-2 rounded-full bg-[#C86B53] animate-pulse" />
            <span className="hidden sm:inline">live</span>
          </span>
        </div>
      </header>
      )}

      {/* Main canvas */}
      <main className="flex-1 relative flex items-center justify-center p-4 overflow-y-auto no-scrollbar">
        {!roomUrl ? (
          <div className="max-w-md w-full text-center">
            <h1 className="text-4xl font-display font-semibold">Your room + our furniture</h1>
            <p className="mt-3 text-white/60">Upload a picture of your room or capture one with your phone. We&rsquo;ll place selected products realistically.</p>
            <div className="mt-8 space-y-3">
              <label className="block cursor-pointer border-2 border-dashed border-white/30 hover:border-[#C86B53] rounded-xl p-8 transition" data-testid="upload-room-input">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadRoom(e.target.files?.[0])} />
                <Upload className="h-6 w-6 mx-auto mb-3 text-white/70" />
                <p className="text-sm">Tap to upload room image</p>
              </label>
              <label className="block cursor-pointer border-2 border-dashed border-white/30 hover:border-[#C86B53] rounded-xl p-4 transition" data-testid="camera-room-input">
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadRoom(e.target.files?.[0])} />
                <p className="text-sm flex items-center justify-center gap-2"><Camera className="h-4 w-4" /> Capture with camera</p>
              </label>
            </div>
          </div>
        ) : (
          <div className="relative w-full max-w-3xl">
            <div className={`flex gap-3 items-start ${renderResult && (renderResult.appliedProducts?.length > 0 || renderResult.appliedPaint) ? "" : ""}`}>
            <div className="flex-1 min-w-0">
            <div className="relative rounded-2xl bg-black flex items-center justify-center" style={{ overflow: roomRotation % 180 !== 0 ? "visible" : "hidden" }} data-testid="room-canvas">
              <img
                ref={imgRef}
                src={resolveMediaUrl(roomUrl)}
                alt="room"
                crossOrigin="anonymous"
                className="block transition-transform duration-300"
                style={{
                  transform: roomRotation ? `rotate(${roomRotation}deg)` : undefined,
                  maxHeight: roomRotation % 180 !== 0 ? "60vw" : "70vh",
                  maxWidth: roomRotation % 180 !== 0 ? "60vh" : "100%",
                  width: "auto",
                  height: "auto",
                }}
                onLoad={(e) => {
                  e.target.dataset.naturalW = e.target.naturalWidth;
                  e.target.dataset.naturalH = e.target.naturalHeight;
                }}
                onError={(e) => {
                  if (!e.target.dataset.retried) {
                    e.target.dataset.retried = "1";
                    e.target.src = resolveMediaUrl(roomUrl) + (roomUrl.includes("?") ? "&" : "?") + "_r=" + Date.now();
                  }
                }}
              />
              {marking && !renderResult && (
                <MarkerOverlay
                  color={MARKER_COLORS.find((c) => c.key === markerColor)?.hex}
                  strokes={strokes}
                  setStrokes={setStrokes}
                  singleMarkerMode={false}
                  brushSize={preciseRemove ? brushSize : undefined}
                />
              )}
            </div>
            {rendering && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl">
                <div className="glass-panel !bg-white/10 !border-white/20 rounded-xl px-6 py-4 text-center">
                  <div className="h-1.5 w-40 rounded-full overflow-hidden bg-white/10">
                    <div className="h-full shimmer-progress w-full" />
                  </div>
                  <p className="mt-3 text-sm text-white/80">Merging furniture into your room…</p>
                  <p className="mt-1 text-xs text-white/40">This takes 15–60 seconds</p>
                </div>
              </div>
            )}

            {/* Marker tool bar — horizontal solid bar docked to the bottom on small screens, vertical glass strip on the right from sm: up */}
            {!renderResult && (
              <div
                className="absolute inset-x-2 bottom-2 sm:inset-x-auto sm:bottom-auto sm:right-3 sm:top-1/2 sm:-translate-y-1/2 glass-panel !bg-black/75 sm:!bg-white/10 !border-white/20 rounded-full p-1.5 sm:p-2 flex flex-row sm:flex-col items-center gap-1 sm:gap-2 overflow-x-auto sm:overflow-visible no-scrollbar max-w-full"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                <input ref={changeRoomRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadRoom(e.target.files?.[0])} />
                <button
                  onClick={() => changeRoomRef.current?.click()}
                  className="p-2 rounded-full text-white/80 hover:bg-white/10 shrink-0"
                  title="Change room photo"
                >
                  <ImagePlus className="h-4 w-4" />
                </button>
                <div className="w-px h-5 sm:h-px sm:w-6 mx-0.5 sm:mx-auto shrink-0 bg-white/20" />
                <button
                  onClick={() => setRoomRotation((r) => (r + 90) % 360)}
                  className="p-2 rounded-full text-white/80 hover:bg-white/10 shrink-0"
                  title="Rotate 90° clockwise"
                >
                  <RotateCw className="h-4 w-4" />
                </button>
                {roomRotation !== 0 && (
                  <button
                    onClick={applyRotation}
                    className="p-1.5 rounded-full bg-[#C86B53] text-white text-[9px] font-semibold leading-none shrink-0"
                    title="Apply rotation"
                  >
                    ✓
                  </button>
                )}
                <div className="w-px h-5 sm:h-px sm:w-6 mx-0.5 sm:mx-auto shrink-0 bg-white/20" />
                {history.length > 1 && (
                  <>
                    <button
                      onClick={() => setShowHistory(true)}
                      className="p-2 rounded-full text-white/80 hover:bg-white/10 shrink-0"
                      title="View session history"
                    >
                      <Images className="h-4 w-4" />
                    </button>
                    <div className="w-px h-5 sm:h-px sm:w-6 mx-0.5 sm:mx-auto shrink-0 bg-white/20" />
                  </>
                )}
                {historyIndex > 0 && (
                  <>
                    <button
                      data-testid="tool-undo-version"
                      onClick={undo}
                      disabled={rendering}
                      className="p-2 rounded-full text-white/80 hover:bg-white/10 disabled:opacity-40 shrink-0"
                      title="Undo last generation"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </button>
                    <div className="w-px h-5 sm:h-px sm:w-6 mx-0.5 sm:mx-auto shrink-0 bg-white/20" />
                  </>
                )}

                <button
                  data-testid="tool-marker-toggle"
                  onClick={() => setMarking((m) => !m)}
                  className={`p-2 rounded-full transition shrink-0 ${marking ? "bg-[#C86B53] text-white" : "text-white/80 hover:bg-white/10"}`}
                  title="Mark a spot"
                >
                  <PenLine className="h-4 w-4" />
                </button>
                {marking && !preciseRemove && (
                  <>
                    <div className="w-px h-5 sm:h-px sm:w-6 mx-0.5 sm:mx-auto shrink-0 bg-white/20" />
                    {MARKER_COLORS.map((c) => (
                      <button
                        key={c.key}
                        data-testid={`marker-color-${c.key}`}
                        onClick={() => setMarkerColor(c.key)}
                        className={`h-5 w-5 rounded-full border-2 transition shrink-0 ${markerColor === c.key ? "border-white scale-110" : "border-white/30"}`}
                        style={{ background: c.hex }}
                        title={c.key}
                      />
                    ))}
                  </>
                )}
                <div className="w-px h-5 sm:h-px sm:w-6 mx-0.5 sm:mx-auto shrink-0 bg-white/20" />
                <button onClick={() => setStrokes(strokes.slice(0, -1))} className="p-2 rounded-full text-white/80 hover:bg-white/10 shrink-0" title="Undo mark"><Undo2 className="h-4 w-4" /></button>
                <button onClick={() => setStrokes([])} className="p-2 rounded-full text-white/80 hover:bg-white/10 shrink-0" title="Clear marks"><Trash2 className="h-4 w-4" /></button>
              </div>
            )}

            {renderResult && renderResult.output_urls?.length > 1 && (
              <div className="mt-4 grid grid-cols-4 gap-2">
                {renderResult.output_urls.map((u, i) => (
                  <img
                    key={i}
                    src={resolveMediaUrl(u)}
                    alt="variation"
                    className={`h-24 w-full object-cover rounded-md cursor-pointer ${u === roomUrl ? "ring-2 ring-[#C86B53]" : ""}`}
                    onClick={() => {
                      setHistory((h) => {
                        const next = [...h];
                        next[historyIndex] = u;
                        return next;
                      });
                      setRenderResult({ ...renderResult, output_urls: [u, ...renderResult.output_urls.filter((x) => x !== u)] });
                    }}
                  />
                ))}
              </div>
            )}
            {renderResult && (
              <div className="mt-4 flex flex-wrap gap-3 justify-center">
                <a data-testid="result-download-btn" href={downloadAssetUrl(roomUrl)} download onClick={() => track("render_downloaded", { format: "jpeg", source: "visualizer" })} className="neivai-btn-primary inline-flex items-center gap-2">
                  <Download className="h-4 w-4" /> Download
                </a>
                <button
                  data-testid="result-share-btn"
                  disabled={sharing}
                  onClick={() => shareRender(roomUrl)}
                  className="neivai-btn-secondary !bg-white/10 !border-white/20 !text-white inline-flex items-center gap-2 disabled:opacity-60"
                >
                  <Share2 className="h-4 w-4" /> {sharing ? "Preparing…" : "Share"}
                </button>
                {historyIndex > 0 && (
                  <button
                    data-testid="result-undo-btn"
                    onClick={undo}
                    className="neivai-btn-secondary !bg-white/10 !border-white/20 !text-white inline-flex items-center gap-2"
                  >
                    <RotateCcw className="h-4 w-4" /> Undo
                  </button>
                )}
                <button
                  data-testid="result-edit-btn"
                  onClick={editAgain}
                  className="neivai-btn-secondary !bg-white/10 !border-white/20 !text-white inline-flex items-center gap-2"
                >
                  <Sparkles className="h-4 w-4" /> Edit again
                </button>
              </div>
            )}
            </div>{/* end flex-1 image column */}

            {/* Right side: products used */}
            {renderResult && (renderResult.appliedProducts?.length > 0 || renderResult.appliedPaint) && (
              <div className="shrink-0 w-20 flex flex-col gap-2 overflow-y-auto max-h-[70vh]">
                <p className="text-[10px] text-white/40 text-center uppercase tracking-wider">Used</p>
                {renderResult.appliedProducts?.map((p) => (
                  <div key={p.id} className="flex flex-col items-center gap-1">
                    <img src={resolveMediaUrl(p.image_url)} alt={p.name} className="h-14 w-14 object-cover rounded-md border border-white/20" />
                    <p className="text-[9px] text-white/60 text-center leading-tight line-clamp-2 w-full px-0.5">{p.name}</p>
                  </div>
                ))}
                {renderResult.appliedPaint && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="h-14 w-14 rounded-md border border-white/20" style={{ background: renderResult.appliedPaint.hex }} />
                    <p className="text-[9px] text-white/60 text-center leading-tight w-full px-0.5">{renderResult.appliedPaint.name}</p>
                  </div>
                )}
              </div>
            )}
            </div>{/* end flex row */}
          </div>
        )}
      </main>

      {roomUrl && !renderResult && lastRenderedUrl && (
        <div className="absolute top-3 left-3 z-20">
          <a
            href={downloadAssetUrl(lastRenderedUrl)}
            download
            title="Download last render"
            className="inline-flex items-center gap-1.5 bg-black/60 hover:bg-black/80 border border-white/20 text-white text-xs rounded-full px-3 py-1.5 transition"
          >
            <Download className="h-3.5 w-3.5" /> Download last render
          </a>
        </div>
      )}
      {/* Bottom prompt bar */}
      {roomUrl && !renderResult && (
        <div className="sticky bottom-0 z-30 p-4 glass-panel !bg-white/10 !border-white/20">
          <div
            className="max-w-3xl mx-auto flex gap-2 mb-3 overflow-x-auto no-scrollbar overscroll-x-contain snap-x snap-proximity sm:flex-wrap sm:overflow-visible"
            style={{ WebkitOverflowScrolling: "touch", scrollBehavior: "smooth" }}
          >
            {ACTIONS.map((a) => (
              <button
                key={a.key}
                data-testid={`action-${a.key}`}
                onClick={() => setAction(a.key)}
                title={a.desc}
                className={`shrink-0 snap-start sm:flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm border whitespace-nowrap transition ${
                  action === a.key ? "bg-[#C86B53] border-[#C86B53] text-white" : "border-white/20 text-white/80 hover:bg-white/10"
                }`}
              >
                <a.icon className="h-3.5 w-3.5" /> {a.label}
              </button>
            ))}
          </div>
          {action === "remove" && (
            <div className="max-w-3xl mx-auto flex flex-wrap items-center gap-3 mb-3 px-1">
              <button
                type="button"
                data-testid="precise-remove-toggle"
                onClick={() => {
                  setPreciseRemove((v) => {
                    const next = !v;
                    if (next) setMarking(true);
                    return next;
                  });
                }}
                title="Experimental: brushes a real removal mask, erases it with a local IOPaint (LaMa) model, then a Flux pass touches up the result"
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition ${
                  preciseRemove ? "bg-[#C86B53] border-[#C86B53] text-white" : "border-white/20 text-white/70 hover:bg-white/10"
                }`}
              >
                <Wand2 className="h-3.5 w-3.5" /> Precise removal (beta)
              </button>
              {preciseRemove && (
                <div className="flex items-center gap-2 text-xs text-white/60">
                  <span>Brush</span>
                  <input
                    type="range"
                    min={10}
                    max={150}
                    value={brushSize}
                    onChange={(e) => setBrushSize(Number(e.target.value))}
                    className="w-28 accent-[#C86B53]"
                    data-testid="removal-brush-size"
                  />
                  <span className="w-10 text-right">{brushSize}px</span>
                </div>
              )}
            </div>
          )}
          <div className="max-w-3xl mx-auto flex flex-col md:flex-row gap-2 items-stretch">
            {action !== "remove" && action !== "adjust" && (
              <button
                data-testid="open-catalog-btn"
                onClick={() => setShowCatalog(true)}
                className="px-4 py-3 rounded-full bg-white/10 border border-white/20 text-sm hover:bg-white/20 transition inline-flex items-center gap-2 whitespace-nowrap"
              >
                <span className="h-2 w-2 rounded-full bg-[#C86B53]" />
                {selected.length ? `${selected.length} selected` : "Pick products"}
              </button>
            )}
            {isSurfaceAction && tileType && (
              <button
                type="button"
                data-testid="tile-type-chip"
                onClick={() => setShowTileTypeModal(true)}
                title="Change tile surface"
                className="px-4 py-3 rounded-full bg-white/10 border border-white/20 text-sm hover:bg-white/20 transition inline-flex items-center gap-2 whitespace-nowrap"
              >
                <span className="h-2 w-2 rounded-full bg-[#C86B53]" />
                {tileType === "wall" ? "Wall Tile" : "Floor Tile"}
              </button>
            )}
            {isSurfaceAction && selectedPaintProduct && colorOverrides[selectedPaintProduct.id] && (
              <button
                type="button"
                data-testid="paint-color-chip"
                onClick={() => { setPaintColorFor(selectedPaintProduct.id); setShowPaintColorModal(true); }}
                title="Change paint color"
                className="px-4 py-3 rounded-full bg-white/10 border border-white/20 text-sm hover:bg-white/20 transition inline-flex items-center gap-2 whitespace-nowrap"
              >
                <span
                  className="h-3.5 w-3.5 rounded-full border border-white/30"
                  style={{ background: colorOverrides[selectedPaintProduct.id] }}
                />
                {colorOverrides[selectedPaintProduct.id]}
              </button>
            )}
            {isSurfaceAction && paintSurface && (
              <button
                type="button"
                data-testid="paint-surface-chip"
                onClick={() => setShowPaintSurfaceModal(true)}
                title="Change paint surface"
                className="px-4 py-3 rounded-full bg-white/10 border border-white/20 text-sm hover:bg-white/20 transition inline-flex items-center gap-2 whitespace-nowrap"
              >
                <span className="h-2 w-2 rounded-full bg-[#C86B53]" />
                {paintSurface === "wall" ? "Wall Paint" : "Ceiling Paint"}
              </button>
            )}
            <input
              data-testid="prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={promptPlaceholder}
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/40"
            />
            <button
              data-testid="visualizer-generate-button"
              onClick={generate}
              disabled={rendering}
              className="neivai-btn-primary inline-flex items-center gap-2 justify-center disabled:opacity-60"
            >
              <Sparkles className="h-4 w-4" /> {rendering ? "Generating…" : "Generate"}
            </button>
          </div>
          {selectedProducts.length > 0 && (
            <div className="max-w-3xl mx-auto mt-3 flex gap-2 overflow-x-auto no-scrollbar pb-1">
              {selectedProducts.map((p) => (
                <div key={p.id} className="shrink-0 relative">
                  <img src={resolveMediaUrl(productImageUrl(p))} alt={p.name} className="h-14 w-14 object-cover rounded-md" />
                  <button onClick={() => toggle(p.id)} className="absolute -top-1 -right-1 bg-white/20 rounded-full p-0.5">
                    <X className="h-3 w-3 text-white" />
                  </button>
                  {colorOverrides[p.id] && (
                    <div
                      data-testid={`selected-color-${p.id}`}
                      title={`Color: ${colorOverrides[p.id]}`}
                      className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full border-2 border-[#1A1C1E]"
                      style={{ background: colorOverrides[p.id] }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="max-w-3xl mx-auto mt-3 text-[10px] leading-relaxed text-white/40 text-center">
            Important: AI-generated visualizations are for design inspiration only. They do not represent exact measurements, scale, spacing, dimensions, or the final installed appearance. Always verify specifications before purchasing or installing furniture.
          </p>
        </div>
      )}

      {/* Tile surface popup — asks whether a just-picked Tile product goes
          on the floor or a wall, since the backend prompt needs to know
          which surface to target and (for a wall) relies on the user's
          marker to say which wall(s). */}
      {showTileTypeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setShowTileTypeModal(false)}>
          <div className="bg-[#1A1C1E] rounded-2xl p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-semibold text-lg">Which surface is this tile for?</h3>
            <p className="mt-1 text-sm text-white/60">This helps us apply it to the right place.</p>
            <div className="mt-5 flex flex-col gap-3">
              <button
                type="button"
                data-testid="tile-type-floor"
                onClick={() => { setTileType("floor"); setShowTileTypeModal(false); }}
                className={`text-left px-4 py-3 rounded-xl border transition ${tileType === "floor" ? "border-[#C86B53] bg-[#C86B53]/10" : "border-white/20 hover:border-white/40 hover:bg-white/5"}`}
              >
                <p className="font-medium text-white">Floor Tile</p>
                <p className="text-xs text-white/50 mt-0.5">Covers the entire floor</p>
              </button>
              <button
                type="button"
                data-testid="tile-type-wall"
                onClick={() => {
                  setTileType("wall");
                  setShowTileTypeModal(false);
                  setMarking(true);
                }}
                className={`text-left px-4 py-3 rounded-xl border transition ${tileType === "wall" ? "border-[#C86B53] bg-[#C86B53]/10" : "border-white/20 hover:border-white/40 hover:bg-white/5"}`}
              >
                <p className="font-medium text-white">Wall Tile</p>
                <p className="text-xs text-white/50 mt-0.5">Mark the wall(s) to tile — left, right, back/front, or a combination</p>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paint color popup — asks for the exact color code directly, right
          after a Paint product is picked. Deliberately its own top-level
          popup rather than the catalog's inline color_palette swatch
          picker used by other product categories: a paint shade shouldn't
          be limited to whatever preset swatches a retailer happened to
          list. Chains straight into the wall/ceiling surface popup once a
          color is confirmed for the first time (skipped on later edits,
          when `paintSurface` is already set). */}
      {showPaintColorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setShowPaintColorModal(false)}>
          <div className="bg-[#1A1C1E] rounded-2xl p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-semibold text-lg">Pick a paint color</h3>
            <p className="mt-1 text-sm text-white/60">Enter the exact color code to apply.</p>
            <PaintColorPicker
              initialHex={colorOverrides[paintColorFor]}
              onCancel={() => setShowPaintColorModal(false)}
              onConfirm={(hex) => {
                setExactColor(paintColorFor, hex);
                setShowPaintColorModal(false);
                if (!paintSurface) setShowPaintSurfaceModal(true);
              }}
            />
          </div>
        </div>
      )}

      {/* Paint surface popup — asks whether a just-picked Paint product's
          chosen color goes on the wall or the ceiling, mirroring the tile
          surface popup above. A wall pick relies on the user's marker to
          say which wall(s), same as wall tile. */}
      {showPaintSurfaceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setShowPaintSurfaceModal(false)}>
          <div className="bg-[#1A1C1E] rounded-2xl p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-semibold text-lg">Where should this paint go?</h3>
            <p className="mt-1 text-sm text-white/60">This helps us apply the color to the right surface.</p>
            <div className="mt-5 flex flex-col gap-3">
              <button
                type="button"
                data-testid="paint-surface-ceiling"
                onClick={() => { setPaintSurface("ceiling"); setShowPaintSurfaceModal(false); }}
                className={`text-left px-4 py-3 rounded-xl border transition ${paintSurface === "ceiling" ? "border-[#C86B53] bg-[#C86B53]/10" : "border-white/20 hover:border-white/40 hover:bg-white/5"}`}
              >
                <p className="font-medium text-white">Ceiling</p>
                <p className="text-xs text-white/50 mt-0.5">Covers the entire ceiling</p>
              </button>
              <button
                type="button"
                data-testid="paint-surface-wall"
                onClick={() => {
                  setPaintSurface("wall");
                  setShowPaintSurfaceModal(false);
                  setMarking(true);
                }}
                className={`text-left px-4 py-3 rounded-xl border transition ${paintSurface === "wall" ? "border-[#C86B53] bg-[#C86B53]/10" : "border-white/20 hover:border-white/40 hover:bg-white/5"}`}
              >
                <p className="font-medium text-white">Wall</p>
                <p className="text-xs text-white/50 mt-0.5">Mark the wall(s) to paint — left, right, back/front, or a combination</p>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session history popup */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setShowHistory(false)}>
          <div className="bg-[#1A1C1E] rounded-2xl p-5 max-w-xl w-full mx-4 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Session History · {history.length} image{history.length !== 1 ? "s" : ""}</h3>
              <button onClick={() => setShowHistory(false)} className="text-white/60 hover:text-white p-1"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex flex-col gap-3 overflow-y-auto">
              {history.map((url, i) => {
                const meta = historyMeta[i] || {};
                const items = [
                  ...(meta.appliedProducts || []),
                  ...(meta.appliedPaint ? [{ id: "paint", name: meta.appliedPaint.name, hex: meta.appliedPaint.hex }] : []),
                ];
                const isCurrent = i === historyIndex;
                return (
                  <div
                    key={i}
                    onClick={() => { setHistoryIndex(i); setRenderResult(null); resetEditState(); setShowHistory(false); }}
                    className={`flex items-start gap-4 p-3 rounded-xl border cursor-pointer transition ${
                      isCurrent ? "border-[#C86B53] bg-[#C86B53]/10" : "border-white/10 hover:border-white/25 bg-white/5"
                    }`}
                  >
                    {/* Thumbnail + download */}
                    <div className="shrink-0 flex flex-col gap-2">
                      <div className="relative w-36 h-24 rounded-lg overflow-hidden">
                        <img src={resolveMediaUrl(url)} alt={i === 0 ? "Original" : `Iteration ${i}`} className="w-full h-full object-cover" />
                      </div>
                      {i > 0 && (
                        <a
                          href={downloadAssetUrl(url)}
                          download
                          onClick={(e) => e.stopPropagation()}
                          title="Download"
                          className="w-36 flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs rounded-lg px-3 py-1.5 transition"
                        >
                          <Download className="h-3.5 w-3.5" /> Download
                        </a>
                      )}
                    </div>
                    {/* Right side */}
                    <div className="flex-1 min-w-0 flex flex-col justify-start gap-2.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-white">{i === 0 ? "Original" : `Iteration ${i}`}</span>
                        {isCurrent && <span className="text-[10px] bg-[#C86B53] text-white rounded-full px-2 py-0.5 leading-none">current</span>}
                      </div>
                      {items.length > 0 ? (
                        <>
                          <p className={`text-[10px] text-white/40 uppercase tracking-wider ${items.length === 1 ? "text-right" : ""}`}>Products used</p>
                          <div className={`flex gap-2.5 overflow-x-auto no-scrollbar ${items.length === 1 ? "justify-end" : ""}`}>
                            {items.map((p) => (
                              <div key={p.id} className="shrink-0 w-36 flex flex-col items-center gap-1">
                                {p.hex
                                  ? <div className="h-24 w-36 rounded-md border border-white/20" style={{ background: p.hex }} />
                                  : <img src={resolveMediaUrl(p.image_url)} alt={p.name} className="h-24 w-36 object-contain bg-white rounded-md border border-white/20" />
                                }
                                <p className="text-[9px] text-white/55 text-center leading-tight line-clamp-2 w-full">{p.name}</p>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <p className="text-xs text-white/25">—</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Product catalog drawer */}
      {showCatalog && (
        <div className="fixed inset-0 z-40 flex items-end md:items-center md:justify-center bg-black/60" onClick={() => setShowCatalog(false)}>
          <div className="w-full md:max-w-3xl md:rounded-2xl bg-white text-[#1A1C1E] max-h-[85vh] flex flex-col rounded-t-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-[#E5E5E5] flex items-center justify-between">
              <div>
                <h3 className="text-xl font-display font-medium">{retailer?.business_name} · Catalog</h3>
                <p className="text-xs text-[#8A8D91]">Pick products to place in your room</p>
              </div>
              <button onClick={() => setShowCatalog(false)} className="p-2 rounded hover:bg-[#F3F1EC]"><X className="h-4 w-4" /></button>
            </div>
            <div className="px-4 py-3 border-b border-[#E5E5E5] flex gap-2 overflow-x-auto no-scrollbar items-center">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`cat-${c.toLowerCase()}`}
                  className={`px-4 py-1.5 rounded-full text-sm border whitespace-nowrap transition shrink-0 ${category === c ? "bg-[#1A1C1E] text-white border-[#1A1C1E]" : "border-[#E5E5E5] text-[#5C5F62] hover:border-[#1A1C1E]"}`}
                >{c}</button>
              ))}
            </div>
            <div
              className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 overflow-y-auto no-scrollbar overscroll-contain flex-1"
              style={{ WebkitOverflowScrolling: "touch", scrollBehavior: "smooth" }}
            >
              {products.length === 0 && <p className="col-span-full text-center text-sm text-[#8A8D91] py-8">No products in this category yet.</p>}
              {products.map((p) => {
                const isOn = selected.includes(p.id);
                // Paint never uses the catalog's preset color_palette
                // swatches — selecting it opens the dedicated color-code
                // popup instead (see toggle()), so this button is hidden
                // for it even if a retailer happened to set one.
                const hasPalette = p.color_palette?.length > 0 && (p.category || "").toLowerCase() !== "paint";
                const hasVariants = p.images?.length > 1;
                const chosenColor = colorOverrides[p.id];
                return (
                  <div
                    key={p.id}
                    onClick={(e) => {
                      if (e.target.closest('button')) return;
                      toggle(p.id);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggle(p.id)}
                    data-testid={`select-product-${p.id}`}
                    className={`relative rounded-lg border transition text-left cursor-pointer flex flex-col ${isOn ? "border-[#C86B53] ring-2 ring-[#C86B53]/30" : "border-[#E5E5E5]"}`}
                  >
                    <div className="relative w-full bg-[#F3F1EC] rounded-t-lg overflow-hidden" style={{paddingBottom:"75%"}}>
                      <img src={resolveMediaUrl(productImageUrl(p))} alt={p.name} className="absolute inset-0 h-full w-full object-cover" />
                    </div>
                    <div className="p-2 bg-white rounded-b-lg flex flex-col gap-0.5">
                      <p className="text-xs font-medium text-[#1A1C1E] leading-tight" style={{display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{p.name}</p>
                      {p.price != null && <p className="text-xs font-semibold text-[#C86B53]">₹{p.price.toLocaleString("en-IN")}</p>}
                      <p className="text-[10px] text-[#8A8D91]">{p.category}</p>
                      {p.dimensions && <p className="text-[10px] text-[#8A8D91] truncate">{p.dimensions}</p>}
                    </div>
                    {isOn && <div className="absolute top-2 right-2 bg-[#C86B53] text-white text-[10px] rounded-full px-2 py-0.5">Selected</div>}
                    {hasVariants && (
                      <button
                        type="button"
                        data-testid={`variant-btn-${p.id}`}
                        title="Choose color/design"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isOn) toggle(p.id);
                          setColorPickerFor(null);
                          setImagePickerFor((cur) => (cur === p.id ? null : p.id));
                        }}
                        className="absolute bottom-2 left-2 h-6 w-6 rounded-full border-2 border-white shadow flex items-center justify-center bg-[#1A1C1E]/70"
                      >
                        <Images className="h-3 w-3 text-white" />
                      </button>
                    )}
                    {hasPalette && (
                      <button
                        type="button"
                        data-testid={`color-btn-${p.id}`}
                        title="Choose color"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isOn) toggle(p.id);
                          setImagePickerFor(null);
                          setColorPickerFor((cur) => (cur === p.id ? null : p.id));
                        }}
                        className="absolute bottom-2 right-2 h-6 w-6 rounded-full border-2 border-white shadow flex items-center justify-center bg-[#1A1C1E]/70"
                        style={chosenColor ? { background: chosenColor } : undefined}
                      >
                        {!chosenColor && <Palette className="h-3 w-3 text-white" />}
                      </button>
                    )}
                    {colorPickerFor === p.id && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`color-popover-${p.id}`}
                        className="absolute bottom-9 right-2 z-10 bg-white rounded-lg shadow-lg border border-[#E5E5E5] p-2 flex gap-1.5"
                      >
                        {p.color_palette.map((hex) => (
                          <button
                            key={hex}
                            type="button"
                            data-testid={`color-swatch-${p.id}-${hex.replace("#", "")}`}
                            onClick={() => {
                              setColor(p.id, hex);
                              setColorPickerFor(null);
                            }}
                            title={hex}
                            className={`h-5 w-5 rounded-full border transition ${chosenColor === hex ? "ring-2 ring-[#C86B53] border-white" : "border-[#E5E5E5]"}`}
                            style={{ background: hex }}
                          />
                        ))}
                      </div>
                    )}
                    {imagePickerFor === p.id && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`variant-popover-${p.id}`}
                        className="absolute bottom-9 left-2 z-10 bg-white rounded-lg shadow-lg border border-[#E5E5E5] p-2 flex gap-1.5"
                      >
                        {p.images.map((img) => (
                          <button
                            key={img.id}
                            type="button"
                            data-testid={`variant-swatch-${p.id}-${img.id}`}
                            title={img.label || "Variant"}
                            onClick={() => {
                              setImageVariant(p.id, img.id);
                              setImagePickerFor(null);
                            }}
                            className={`h-9 w-9 rounded-md overflow-hidden border-2 transition ${imageOverrides[p.id] === img.id ? "border-[#C86B53]" : "border-transparent"}`}
                          >
                            <img src={resolveMediaUrl(img.url)} alt={img.label || "variant"} className="h-full w-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="p-4 border-t border-[#E5E5E5] flex items-center justify-between">
              <p className="text-sm text-[#5C5F62]">{selected.length} selected</p>
              <button onClick={() => setShowCatalog(false)} className="neivai-btn-primary">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Hex code entry for the paint color popup: a native color swatch input
 * kept in sync with a plain hex text field, so a shopper can either eyeball
 * a color or type/paste an exact code (e.g. from a paint brand's shade
 * card). Confirm is disabled until the text field holds a valid #RRGGBB. */
function PaintColorPicker({ initialHex, onConfirm, onCancel }) {
  const [hex, setHex] = useState(HEX_RE.test(initialHex) ? initialHex : "#C86B53");
  const valid = HEX_RE.test(hex);

  return (
    <div className="mt-5">
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={valid ? hex : "#C86B53"}
          onChange={(e) => setHex(e.target.value)}
          data-testid="paint-color-swatch-input"
          title="Pick a color"
          className="h-12 w-12 shrink-0 rounded-lg border border-white/20 cursor-pointer p-0.5 bg-transparent"
        />
        <input
          value={hex}
          onChange={(e) => setHex(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && valid) onConfirm(hex); }}
          placeholder="#3B5D50"
          data-testid="paint-color-hex-input"
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/40"
        />
      </div>
      {!valid && (
        <p className="mt-1.5 text-xs text-[#D64545]">Enter a valid hex color, e.g. #3B5D50</p>
      )}
      <div className="mt-5 flex gap-3">
        <button type="button" onClick={onCancel} className="flex-1 px-4 py-2.5 rounded-xl border border-white/20 text-white/80 hover:bg-white/5 transition">
          Cancel
        </button>
        <button
          type="button"
          data-testid="paint-color-confirm-btn"
          disabled={!valid}
          onClick={() => onConfirm(hex)}
          className="flex-1 px-4 py-2.5 rounded-xl bg-[#C86B53] text-white disabled:opacity-50 transition"
        >
          Use this color
        </button>
      </div>
    </div>
  );
}

/** Bounding-box area of a stroke in display pixels — used to pick the
 * largest mark when only one is allowed (add + single product). */
function strokeArea(stroke) {
  if (!stroke.points.length) return 0;
  const xs = stroke.points.map((p) => p.x);
  const ys = stroke.points.map((p) => p.y);
  const w = Math.max(...xs) - Math.min(...xs);
  const h = Math.max(...ys) - Math.min(...ys);
  return Math.max(w * h, 1);
}

/** Freehand marker overlay — semi-transparent thick strokes so the room
 * context stays readable while the mark is clearly visible. Single-point
 * taps render as a filled circle. Display style matches what gets
 * composited onto the image in compositeMarkedRoom(). */
function MarkerOverlay({ color, strokes, setStrokes, brushSize }) {
  const [drawing, setDrawing] = useState(false);
  const [current, setCurrent] = useState(null);
  const ref = useRef(null);
  // Purely a display preview of the mask brush size in "Precise removal"
  // mode — the actual mask is rasterized separately (compositeRemovalMask)
  // at the room photo's natural resolution. Falls back to the original
  // fixed 6px stroke / 5px tap radius when unset, so every other marking
  // mode is visually unchanged (those two were never tied to each other,
  // so brushSize covers both rather than picking one as "the" default).
  const strokeDisplayWidth = brushSize || 6;
  const tapDisplayRadius = brushSize ? brushSize / 2 : 5;

  const pos = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const t = e.touches?.[0];
    const clientX = t?.clientX ?? e.clientX;
    const clientY = t?.clientY ?? e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const down = (e) => {
    e.preventDefault();
    const p = pos(e);
    setDrawing(true);
    setCurrent({ points: [p], color });
  };
  const move = (e) => {
    if (!drawing || !current) return;
    const p = pos(e);
    setCurrent({ ...current, points: [...current.points, p] });
  };
  const up = () => {
    if (current) {
      setStrokes([...strokes, current]);
    }
    setCurrent(null);
    setDrawing(false);
  };

  const items = current ? [...strokes, current] : strokes;

  return (
    <svg
      ref={ref}
      className="absolute inset-0 h-full w-full cursor-crosshair"
      onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}
      onTouchStart={down} onTouchMove={move} onTouchEnd={up}
    >
      {items.map((s, i) => {
        if (s.points.length === 1) {
          // Single tap → filled circle
          return (
            <circle
              key={i}
              cx={s.points[0].x}
              cy={s.points[0].y}
              r={tapDisplayRadius}
              fill={s.color}
              opacity="0.8"
            />
          );
        }
        const d = s.points.map((p, j) => `${j === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
        return (
          <path
            key={i}
            d={d}
            stroke={s.color}
            strokeWidth={strokeDisplayWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            opacity="0.8"
          />
        );
      })}
    </svg>
  );
}
